import { Trans } from 'react-i18next';
import { t } from 'i18next';

import { DocumentLogModel, FilterOptionModel } from 'models';
import {
  ColumnProps,
  LeoDateInterval,
  LeoSelectFieldMulti,
  Page,
  Table,
  useFilterConfig,
  usePanelState,
  useTableQueryParams,
} from 'components';
import LeoTextField from 'components/input-fields/leoTextField/LeoTextField';
import { useFieldDataAndAccessor } from 'utils/useFieldDataAndAccessor';
import { getPageName } from 'utils/format';
import { formatDate } from 'utils/dates';
import { useDocumentLogList, useDocumentLogAsyncExcelExport } from 'features/documentLog';
import { RouteNames } from 'config/routeNames';

import { ReactComponent as Export } from 'assets/icons/Material/fileDownload.svg';
import { validationSchema } from 'pages/Kozos/Statistics/EventLog/EventLogPage';
import { getValueSetValues } from 'utils/valueSetFunctions';
import { useValueSetList } from 'features/valueSets';

const DocumentLogPage = () => {
  const {
    onPageChange,
    onRowsPerPageChange,
    onLoadQuery,
    onFilter,
    onSort,
    params,
    apiParams,
    exportParams,
    onTableColumnVisibilityChange,
  } = useTableQueryParams();

  const { data: documentLog, isLoading, isFetching, dataUpdatedAt } = useDocumentLogList(apiParams);

  const { data: valueSet, isLoading: isValueSetLoading } = useValueSetList({
    code: ['KT'],
  });

  const { fetch: onExport, isLoading: isExportInProgress } =
    useDocumentLogAsyncExcelExport(exportParams);

  const { activePanel, closePanel, setPanel } = usePanelState();

  const { data: publicUtilityTypeData, accessor: publicUtilityTypeAccessor } =
    useFieldDataAndAccessor(getValueSetValues(valueSet, 'KT'));

  const headerAction = [
    {
      name: 'export',
      label: 'COMMON.EXPORT',
      icon: <Export fill="currentColor" />,
      onClick: () => onExport(),
      isLoading: isExportInProgress,
      disabledIf: !exportParams?.filter,
    },
  ];

  const filters: FilterOptionModel[] = [
    {
      headerTitle: 'LEO_BIZONYLATOK_NAPLOJA.OPEM_ARRIVAL_DATE',
      component: <LeoDateInterval name="opemArrivalDate" />,
      name: 'opemArrivalDate',
      panelFieldWidth: 12,
      panelFieldOffset: 12,
      divider: true,
      abbrName: t('FILTERABBR.OPEM_ARRIVAL_DATE'),
    },
    {
      headerTitle: 'LEO_BIZONYLATOK_NAPLOJA.PUBLIC_UTILITY_TYPE',
      component: (
        <LeoSelectFieldMulti
          name="publicUtilityTypes"
          data={publicUtilityTypeData}
          accessor={publicUtilityTypeAccessor}
          label="LEO_BIZONYLATOK_NAPLOJA.PUBLIC_UTILITY_TYPE"
        />
      ),
      name: 'publicUtilityTypes',
      panelFieldWidth: 12,
      divider: true,
      abbrName: t('FILTERABBR.PUBLIC_UTILITY_TYPE'),
    },
    {
      headerTitle: 'LEO_BIZONYLATOK_NAPLOJA.DOCUMENT_NUMBER',
      component: (
        <LeoTextField
          name="documentNumber"
          label={<Trans i18nKey="LEO_BIZONYLATOK_NAPLOJA.DOCUMENT_NUMBER" />}
        />
      ),
      name: 'documentNumber',
      panelFieldWidth: 12,
      divider: true,
      abbrName: t('FILTERABBR.DOCUMENT_NUMBER'),
    },
    {
      headerTitle: 'LEO_BIZONYLATOK_NAPLOJA.BINDING',
      component: (
        <LeoTextField
          name="bindingId"
          label={<Trans i18nKey="LEO_BIZONYLATOK_NAPLOJA.BINDING" />}
        />
      ),
      name: 'bindingId',
      panelFieldWidth: 12,
      divider: true,
      abbrName: t('FILTERABBR.WIRING'),
    },
    {
      headerTitle: 'LEO_BIZONYLATOK_NAPLOJA.SERIAL_NUMBER',
      component: (
        <LeoTextField
          name="serialNumber"
          label={<Trans i18nKey="LEO_BIZONYLATOK_NAPLOJA.SERIAL_NUMBER" />}
        />
      ),
      name: 'serialNumber',
      panelFieldWidth: 12,
      abbrName: t('FILTERABBR.SERIAL_NUMBER'),
    },
  ];

  const filterConfig = useFilterConfig({
    params,
    onFilter,
    filters,
    onLoad: onLoadQuery,
    isButton: true,
    pageName: getPageName(RouteNames.LEO_BIZONYLATOK_NAPLOJA),
    validationSchema,
    isFetching,
  });

  const panels = [filterConfig];

  const columns: ColumnProps<DocumentLogModel>[] = [
    {
      key: 'publicUtilityType',
      header: <Trans i18nKey="LEO_BIZONYLATOK_NAPLOJA.PUBLIC_UTILITY_TYPE" />,
      accessor: ({ publicUtilityType: utilityType }) => publicUtilityTypeAccessor[utilityType],
      field: 'publicUtilityType',
      sortable: true,
    },
    {
      key: 'documentNumber',
      header: <Trans i18nKey="LEO_BIZONYLATOK_NAPLOJA.DOCUMENT_NUMBER" />,
      accessor: 'documentNumber',
      sortable: true,
    },
    {
      key: 'bindingId',
      header: <Trans i18nKey="LEO_BIZONYLATOK_NAPLOJA.BINDING" />,
      accessor: 'bindingId',
      sortable: true,
    },
    {
      key: 'serialNumber',
      header: <Trans i18nKey="LEO_BIZONYLATOK_NAPLOJA.SERIAL_NUMBER" />,
      accessor: 'serialNumber',
      sortable: true,
    },
    {
      key: 'opemArrivalDate',
      header: <Trans i18nKey="LEO_BIZONYLATOK_NAPLOJA.OPEM_ARRIVAL_DATE" />,
      accessor: ({ opemArrivalDate }) => formatDate(opemArrivalDate),
      field: 'opemArrivalDate',
      sortable: true,
    },
    {
      key: 'availabilityDate',
      header: <Trans i18nKey="LEO_BIZONYLATOK_NAPLOJA.AVAILABILITY_DATE" />,
      accessor: ({ availabilityDate }) => formatDate(availabilityDate),
      field: 'availabilityDate',
      sortable: true,
    },
    {
      key: 'lastTransferDate',
      header: <Trans i18nKey="LEO_BIZONYLATOK_NAPLOJA.LAST_TRANSFER_DATE" />,
      accessor: ({ lastTransferDate }) => formatDate(lastTransferDate),
      field: 'lastTransferDate',
      sortable: true,
    },
    {
      key: 'deviceDownloadDate',
      header: <Trans i18nKey="LEO_BIZONYLATOK_NAPLOJA.DEVICE_DOWNLOAD_DATE" />,
      accessor: ({ deviceDownloadDate }) => formatDate(deviceDownloadDate),
      field: 'deviceDownloadDate',
      sortable: true,
    },
    {
      key: 'firstNloDate',
      header: <Trans i18nKey="LEO_BIZONYLATOK_NAPLOJA.FIRST_NLO_DATE" />,
      accessor: ({ firstNloDate }) => formatDate(firstNloDate),
      field: 'firstNloDate',
      sortable: true,
    },
    {
      key: 'readingDate',
      header: <Trans i18nKey="LEO_BIZONYLATOK_NAPLOJA.READING_DATE" />,
      accessor: ({ readingDate }) => formatDate(readingDate),
      field: 'readingDate',
      sortable: true,
    },
    {
      key: 'recallDate',
      header: <Trans i18nKey="LEO_BIZONYLATOK_NAPLOJA.RECALL_DATE" />,
      accessor: ({ recallDate }) => formatDate(recallDate),
      field: 'recallDate',
      sortable: true,
    },
    {
      key: 'closingDate',
      header: <Trans i18nKey="LEO_BIZONYLATOK_NAPLOJA.CLOSING_DATE" />,
      accessor: ({ closingDate }) => formatDate(closingDate),
      field: 'closingDate',
      sortable: true,
    },
  ];

  return (
    <Page
      panels={panels}
      activePanel={activePanel}
      title={<Trans i18nKey="LEO_BIZONYLATOK_NAPLOJA.TITLE" />}
      inScroll
      closePanel={closePanel}
      setPanel={setPanel}
    >
      {!isValueSetLoading && (
        <Table
          filterDetailsData={{ publicUtilityTypeAccessor }}
          keyField="id"
          panels={panels}
          activePanel={activePanel}
          setPanel={setPanel}
          onTableColumnVisibilityChange={onTableColumnVisibilityChange}
          list={documentLog?.data || []}
          total={documentLog?.meta?.total}
          timestamp={dataUpdatedAt}
          enableCheckbox={false}
          enableTableView={true}
          loading={isLoading}
          params={params}
          onSort={onSort}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          columns={columns}
          pageHeaderActions={headerAction}
          onLoadQuery={onLoadQuery}
        />
      )}
    </Page>
  );
};

export default DocumentLogPage;
